<template>
  <div class="p-none">
    <FilterPanel
      v-model="filter"
      @update="update"
      @create="onCreate"
      @destroy="onDestroy"
      @add="onAdd"
      @remove="onRemove"
    />
    <ItemsTable
      :items="items"
      :total="total"
      :loading="loading"
      class="mt-1"
      @destroy="destroy"
      @clone="clone"
      @edit="edit"
    />
    <Pagination
      class="mt-1"
      :total="total"
      :page="page"
      :limit="paginationLimit"
      @pagination="update"
    />
    <Modal
      :visible="showModal"
      :loading="loading"
      :mode="mode"
      @close="closeModal"
      @save="onSubmit"
    />
  </div>
</template>

<script lang="ts">
import { computed, ref, reactive, defineComponent, onMounted } from 'vue'
import ItemsTable from './IPsListsTab/ItemsTable.vue'
import FilterPanel from './IPsListsTab/FilterPanel.vue'
import Modal from './IPsListsTab/Modal.vue'

import { useIPListsStore } from '@/apps/traforama/store/ipLists'
import {
  IIPListPayload,
  IIPListFilter,
  IIPListAttributes
} from '@/apps/traforama/interfaces/ipLists'
import Pagination from '@/components/Pagination.vue'
import { LIMIT } from '@/constants'
import useResize from '@/hooks/useResize'
import useValidation from '@/hooks/useValidation'
import { CampaignValidationErrorsKey } from '@/api/types'
import { safeSubmitProcessing } from '@/utils/helpers'
import { ElMessage } from 'element-plus'

const SAVE_MODE = 'save'
const ADD_MODE = 'add'
const REMOVE_MODE = 'remove'
const CLONE_MODE = 'clone'

export default defineComponent({
  name: 'IPListsTab',
  components: {
    ItemsTable,
    FilterPanel,
    Modal,
    Pagination
  },
  props: {},
  setup(props, context) {
    const ipListsStore = useIPListsStore()

    const total = computed(() => ipListsStore.total)
    const items = computed(() => ipListsStore.currentItems)
    const page = computed(() => ipListsStore.currentPage)
    const loading = computed(() => ipListsStore.listLoading)
    const filter = computed({
      get: () => ipListsStore.filter,
      set: (val: IAdScanFilter) => ipListsStore.SetFilters(val)
    })

    const limit = ref(LIMIT)
    const { isDesktop } = useResize()
    const showModal = ref(false)
    const mode = ref(SAVE_MODE)
    const { provideValidation, setValidationErrors } = useValidation(
      CampaignValidationErrorsKey
    )
    const errors = provideValidation()

    const update = ({
      limit = 15,
      newPage = 1
    }: {
      limit?: number
      newPage?: number
    }) => {
      ipListsStore.SetCurrentPage(newPage)
      ipListsStore.FetchItems()
    }

    const load = (id: string) => {
      loading.value = true
      ipListsStore.FetchItem(id).then(() => {
        loading.value = false
      })
    }

    const paginationLimit = computed(() => {
      return items.value.length > LIMIT ? items.value.length : LIMIT
    })

    const onCreate = () => {
      mode.value = SAVE_MODE
      ipListsStore.SetCurrent(null)
      showModal.value = true
    }
    const onDestroy = () => {
      ipListsStore.DestroyItems(Object.keys(ipListsStore.selected)).then(() => {
        ElMessage.success({
          message: 'IPs lists has been archived',
          type: 'success'
        })
        update({ newPage: 1 })
      })
    }
    const onAdd = () => {
      onCreate()
      mode.value = ADD_MODE
    }
    const onRemove = () => {
      onCreate()
      mode.value = REMOVE_MODE
    }

    const destroy = (id: string) => {
      ipListsStore.DestroyItem(id).then(() => {
        ElMessage.success({
          message: 'IPs list has been archived',
          type: 'success'
        })
        update({ newPage: 1 })
      })
    }

    const edit = (id: string) => {
      loading.value = true
      ipListsStore.FetchItem(id).then(() => {
        loading.value = false
        showModal.value = true
        mode.value = SAVE_MODE
      })
    }

    const clone = (id: string) => {
      loading.value = true
      ipListsStore.FetchItem(id).then(() => {
        loading.value = false
        showModal.value = true
        mode.value = CLONE_MODE
      })
    }

    const closeModal = () => {
      showModal.value = false
    }

    const onSuccess = () => {
      closeModal()
      update({ newPage: 1 })
    }

    const onSubmit = async (item: IIPListAttributes) => {
      errors.value = {}
      if (mode.value === CLONE_MODE) {
        delete item['id']
      }

      let saveFunc = () => ipListsStore.SaveItem(item)
      if ([ADD_MODE, REMOVE_MODE].includes(mode.value)) {
        saveFunc = () =>
          ipListsStore.BulkUpdate({
            ip_string: item.ip_string,
            mode: mode.value,
            ids: Object.keys(ipListsStore.selected)
          })
      }
      await safeSubmitProcessing(
        loading,
        saveFunc,
        () => onSuccess(),
        (err) => setValidationErrors(err, errors)
      )
    }

    onMounted(() => {
      update({ newPage: page.value })
    })

    return {
      loading,
      page,
      update,
      items,
      total,
      load,
      filter,
      paginationLimit,
      isDesktop,
      showModal,
      closeModal,
      onCreate,
      onDestroy,
      onAdd,
      onRemove,
      onSubmit,
      destroy,
      clone,
      edit,
      mode
    }
  }
})
</script>
